import { FlectoLanguages } from '../types/FlectoLanguages'

/**
 * Translation files to be loaded.
 *
 * If you need to include a new file, create the
 * JSON under the desired folder suffixed with the locale (e.g. .en.json) and
 * add it to the list.
 */
export const files = {
  link: ['booking', 'handlers', 'sidebar', 'tabs'],
  saas: [
    'booking_actions',
    'booking_customer',
    'booking_dates',
    'booking_documents',
    'booking_insurances',
    'booking_items',
    'booking_payments',
    'booking_pickup_return',
    'booking_services',
    'booking_sidebar',
    'booking_value',
    'booking_invoicing',
    'booking',
    'bookings',
    'create-booking-payments',
    'create-booking-pickup',
    'create-booking-return',
    'create-booking-services',
    'create-booking',
    'customers',
    'dates',
    'flecto-link',
    'general',
    'home',
    'image-manager',
    'inquiries',
    'insurances',
    'item_categories',
    'item-detail',
    'item-form',
    'item',
    'items',
    'location',
    'login',
    'maintenance',
    'mobile',
    'modal-filter-table',
    'nav',
    'notifications',
    'onboarding',
    'products',
    'products-tracking',
    'profile_account',
    'profile_address',
    'profile_billing',
    'profile_bookings',
    'profile_business',
    'profile_inventory',
    'profile_locations',
    'profile_mobile',
    'profile_store',
    'profile_taxes',
    'profile_invoicing',
    'profile',
    'recover-password',
    'saas-nav',
    'schedule',
    'signup',
    'sort',
    'table',
    'user-menu',
    'validation',
    'verification',
  ],
  shared: [
    'actions',
    'basket',
    'confirmation-page',
    'countries',
    'data_table',
    'document_categories',
    'file_uploader',
    'filters',
    'forms',
    'locales_supported',
    'shared',
    'validation',
  ],
  store: [
    'store-page-category',
    'store-page-checkout',
    'store-page-confirmation',
    'store-page-home',
    'store-page-product-list',
    'store-page-product',
    'store-page-search',
    'store-page-terms',
    'store-shared',
  ],
}

/**
 * Load all translation files based on files object above. The result is an
 * object organized by locale and app, as follows:
 *
 * @returns
 * ```
 * {
 *   en: {
 *     link: {
 *       booking: { file json content },
 *       handlers: { file json content },
 *       sidebar: { file json content },
 *       tabs: { file json content },
 *       ...
 *     },
 *     saas: { ... },
 *     shared: { ... },
 *     store: { ... },
 *   },
 *   es: {
 *     link: { ... },
 *     saas: { ... },
 *     shared: { ... },
 *     store: { ... },
 *   },
 *   ...
 * }
 * ```
 */
type Apps = keyof typeof files
type File = (typeof files)[Apps][number]
type TranslationObject = Record<`${FlectoLanguages}`, Record<Apps, Record<File, Record<string, string>>>>

export function loadTranslations(): TranslationObject {
  const translations = new Map()
  const locales = [...new Set(Object.values(FlectoLanguages))]

  locales.forEach((locale) => {
    const appAcc = new Map()

    Object.keys(files).forEach((app) => {
      const fileAcc = new Map()

      // @ts-expect-error Missing type
      files[app].forEach((fileName: string) => {
        try {
          fileAcc.set(fileName, require(`./${app}/${fileName}.${locale}.json`))
        } catch {
          console.error(
            `Missing JSON file: ./${app}/${fileName}.${locale}.json`
          )
          fileAcc.set(fileName, {})
        }
      })

      appAcc.set(app, Object.fromEntries(fileAcc))
    })

    translations.set(locale, Object.fromEntries(appAcc))
  })

  return Object.fromEntries(translations)
}
